import {
	FormControlLabelProps,
	FormControlProps,
	FormHelperTextProps,
	InputLabelProps,
	SelectProps,
	SwitchProps,
	TextFieldProps,
} from '@mui/material';
import { FormikContextType } from 'formik';
import { MouseEventHandler, ReactNode, SyntheticEvent } from 'react';
import cn from 'classnames';
import { LabelProps } from 'shared/_ui/form/types';

type FieldComponentP = SwitchProps | TextFieldProps | SelectProps;

type FieldComponentProps<T extends FieldComponentP> = T & {
	variant?: 'filled' | 'standard' | 'outlined' | undefined;
	readOnly?: boolean;
	onReadOnlyClick?: () => void;
	labelProps?: LabelProps;
};

type FieldControlPropsPrimary =
	| 'color'
	| 'fullWidth'
	| 'focused'
	| 'required'
	| 'sx'
	| 'size'
	| 'variant'
	| 'hiddenLabel'; // |'onClick';
type FormControlLabelPropsPrimary = 'label' | 'control' | 'checked';
type FormInputLabelPropsPrimary = 'children'; // | 'control' | 'checked';
type FormHelperTextPropsPrimary = 'error' | 'children' | 'focused' | 'required';

export type FieldProps<T extends FieldComponentP> = Pick<
	Omit<FormControlProps, 'children'>,
	FieldControlPropsPrimary
> &
	FieldComponentProps<T> & {
		formControlProps?: Omit<FormControlProps, 'children' | FieldControlPropsPrimary>;
		formControlLabelProps?: Omit<FormControlLabelProps, FormControlLabelPropsPrimary>;
		formHelperTextProps?: Omit<FormHelperTextProps, FormHelperTextPropsPrimary>;
		formInputLabelProps?: Omit<InputLabelProps, FormInputLabelPropsPrimary>;
		formik: FormikContextType<any>;
		name: string;
		label?: ReactNode;
	};

// eslint-disable-next-line import/prefer-default-export
export function getFieldProps<T extends FieldComponentP>(props: FieldProps<T>) {
	const {
		formControlProps,
		formHelperTextProps,
		formInputLabelProps,
		formControlLabelProps,
		formik,
		name,
		label,
		hiddenLabel,
		focused,
		required,
		sx,
		size,
		variant,
		onClick: onClickProp,
		readOnly,
		onReadOnlyClick,
		onChange: onChangeProp,
		...rest
	} = props;
	const { checked } = rest as SwitchProps;
	const { fullWidth, labelProps, ...fieldProps } = rest;
	const { color, disabled } = fieldProps;

	const helperText = formik.touched[name] && (formik.errors[name] as TextFieldProps['helperText']);
	const error = formik.touched[name] && Boolean(formik.errors[name]);
	const value = formik.values[name];

	const onClick: MouseEventHandler<HTMLElement> | undefined = readOnly
		? onReadOnlyClick ?? onClickProp
		: onClickProp;
	const onChange =
		(onChangeProp as (event: SyntheticEvent<Element, Event>, checked: boolean) => void) ??
		formik.handleChange;
	const onBlur = formik.handleBlur;

	const formControl: FormControlProps = {
		...formControlProps,
		className: cn('field', formControlProps?.className),
		error,
		fullWidth,
		color,
		disabled,
		focused,
		hiddenLabel,
		required,
		size,
		sx,
		variant,
		onClick,
	};

	const formControlLabel: Omit<FormControlLabelProps, 'control'> = {
		labelPlacement: 'start',
		...formControlLabelProps,
		label,
		disabled,
		checked,
		name,
		// onChange?: (event: React.SyntheticEvent, checked: boolean) => void;
		// onClick,
		onChange,
		value,
	};

	const formHelperText: FormHelperTextProps = {
		...formHelperTextProps,
		id: `${name}-helper-text`,
		children: helperText,
		error,
		disabled,
		focused,
		required,
		variant,
	};

	const formInputLabel: InputLabelProps = {
		...formInputLabelProps,
		htmlFor: props.name,
		id: `${props.name}-label`,
		children: props.label,
	};

	const inputProps = { ...fieldProps, name: props.name };
	const field: T = {
		...(fieldProps as T),
		checked,
		size,
		variant,
		value,
		// onClick,
		onChange,
		onBlur,
		readOnly,
	};

	return {
		formControlProps: formControl,
		formControlLabelProps: formControlLabel,
		formHelperTextProps: formHelperText,
		formInputLabelProps: formInputLabel,
		fieldProps: field,
		inputProps,
	};
}

export function getFieldGridSx(
	params: { labelProps?: LabelProps } & Pick<TextFieldProps, 'helperText' | 'label' | 'fullWidth'>
) {
	const { helperText, label, labelProps, fullWidth } = params;
	let firstRow = 'value';
	let secondRow = helperText ? 'help' : '';
	if (label) {
		if (labelProps?.position === 'top') {
			firstRow = `label" "${firstRow}`;
		} else {
			firstRow = `label ${firstRow}`;
			secondRow = secondRow ? `. ${secondRow}` : '';
		}
	}
	const labelWidth = labelProps?.width ? labelProps?.width : 'min-content';

	return {
		display: fullWidth ? 'grid' : 'inline-grid',
		gridTemplateColumns: `${label && labelProps?.position !== 'top' ? labelWidth : ''} 1fr`,
		gridTemplateAreas: `"${firstRow}"${secondRow ? `"${secondRow}"` : ''}`,
	};
}
